/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Action, ActionCreators } from './actions';
import ServiceTierRequest from '../../shared/entities/serviceTierRequest';

export interface State {
  readonly serviceTierRequests: { [id: string]: ServiceTierRequest };
  readonly isRequestingServiceTierRequests: boolean;
  readonly requestingServiceTierRequestsError?: string;

  readonly isUpdatingServiceTierRequest: boolean;
  readonly updatingServiceTierRequestError?: string;

  readonly currentServiceTierChangeRequest: ServiceTierRequest | null | undefined;
  readonly isRequestingCurrentServiceTierChangeRequest: boolean;
  readonly requestingCurrentServiceTierChangeRequestError?: string;

  readonly isRequestingPostServiceTierRequest: boolean;
  readonly requestingPostServiceTierRequestSuccess?: boolean;
  readonly requestingPostServiceTierRequestError?: string;

  readonly isDeletingServiceTierChangeRequest: boolean;
  readonly requestingDeleteServiceTierChangeRequestSuccess?: boolean;
  readonly requestingDeleteServiceTierChangeRequestError?: string;

  readonly availableServiceTiers: string[];
  readonly isRequestingAvailableServiceTiers: boolean;
  readonly requestingAvailableServiceTiersError?: string;
}

export const initialServiceTierRequestsState: State = {
  serviceTierRequests: {},
  isRequestingServiceTierRequests: false,
  requestingServiceTierRequestsError: undefined,

  isUpdatingServiceTierRequest: false,
  updatingServiceTierRequestError: undefined,

  currentServiceTierChangeRequest: undefined,
  isRequestingCurrentServiceTierChangeRequest: false,
  requestingCurrentServiceTierChangeRequestError: undefined,

  isRequestingPostServiceTierRequest: false,
  requestingPostServiceTierRequestSuccess: undefined,
  requestingPostServiceTierRequestError: undefined,

  isDeletingServiceTierChangeRequest: false,
  requestingDeleteServiceTierChangeRequestSuccess: undefined,
  requestingDeleteServiceTierChangeRequestError: undefined,

  availableServiceTiers: [],
  isRequestingAvailableServiceTiers: false,
  requestingAvailableServiceTiersError: undefined,
};

// Reducer
export default function reducer(state: State = initialServiceTierRequestsState, action: Action): State {
  let partialState: Partial<State> | undefined;
  switch (action.type) {
    case ActionCreators.RequestServiceTierRequests.type:
      partialState = {
        isRequestingServiceTierRequests: true,
        requestingServiceTierRequestsError: undefined,
      };
      break;

    case ActionCreators.ReceivedServiceTierRequestsSuccess.type: {
      const compiledServiceTierRequests: { [id: string]: ServiceTierRequest } = {};

      action.payload.response.forEach((serviceTierRequest: ServiceTierRequest) => {
        compiledServiceTierRequests[serviceTierRequest.id] = serviceTierRequest;
      });

      partialState = {
        serviceTierRequests: compiledServiceTierRequests,
        requestingServiceTierRequestsError: undefined,
        isRequestingServiceTierRequests: false,
      };
      break;
    }
    case ActionCreators.ReceivedServiceTierRequestsError.type:
      partialState = {
        requestingServiceTierRequestsError: action.payload,
        isRequestingServiceTierRequests: false,
      };
      break;
    case ActionCreators.UpdateServiceTierRequest.type:
      partialState = {
        isUpdatingServiceTierRequest: true,
        updatingServiceTierRequestError: undefined,
      };
      break;
    case ActionCreators.UpdateServiceTierRequestSuccess.type: {
      const updatedServiceRequest = action.payload.response as ServiceTierRequest;
      let currentServiceRequests = state.serviceTierRequests;

      currentServiceRequests[updatedServiceRequest.id] = updatedServiceRequest;
      partialState = {
        serviceTierRequests: currentServiceRequests,
        updatingServiceTierRequestError: undefined,
        isUpdatingServiceTierRequest: false,
      };
      break;
    }
    case ActionCreators.UpdateServiceTierRequestError.type:
      partialState = {
        updatingServiceTierRequestError: action.payload,
        isUpdatingServiceTierRequest: false,
      };
      break;
    case ActionCreators.ClearReceivedServiceTierRequestsError.type:
      partialState = {
        requestingServiceTierRequestsError: undefined,
        updatingServiceTierRequestError: undefined,
      };
      break;

    case ActionCreators.RequestCurrentServiceTierChangeRequest.type:
      partialState = {
        isRequestingCurrentServiceTierChangeRequest: true,
        requestingCurrentServiceTierChangeRequestError: undefined,
      };
      break;

    case ActionCreators.ReceivedCurrentServiceTierChangeRequestSuccess.type: {
      partialState = {
        currentServiceTierChangeRequest: action.payload.response,
        requestingCurrentServiceTierChangeRequestError: undefined,
        isRequestingCurrentServiceTierChangeRequest: false,
      };
      break;
    }
    case ActionCreators.ReceivedCurrentServiceTierChangeRequestError.type:
      partialState = {
        requestingCurrentServiceTierChangeRequestError: action.payload,
        isRequestingCurrentServiceTierChangeRequest: false,
      };
      break;
    case ActionCreators.ClearReceivedCurrentServiceTierChangeRequestError.type:
      partialState = {
        requestingCurrentServiceTierChangeRequestError: undefined,
      };
      break;

    case ActionCreators.PostServiceTierRequest.type:
      partialState = {
        isRequestingPostServiceTierRequest: true,
        requestingPostServiceTierRequestSuccess: undefined,
        requestingPostServiceTierRequestError: undefined,
      };
      break;
    case ActionCreators.ReceivedPostServiceTierRequestSuccess.type:
      partialState = {
        isRequestingPostServiceTierRequest: false,
        requestingPostServiceTierRequestSuccess: true,
        requestingPostServiceTierRequestError: undefined,
        currentServiceTierChangeRequest: action.payload,
      };
      break;
    case ActionCreators.ReceivedPostServiceTierRequestError.type:
      partialState = {
        isRequestingPostServiceTierRequest: false,
        requestingPostServiceTierRequestSuccess: false,
        requestingPostServiceTierRequestError: action.payload,
      };
      break;
    case ActionCreators.ClearReceivedPostServiceTierRequestStatus.type:
      partialState = {
        requestingPostServiceTierRequestSuccess: undefined,
        requestingPostServiceTierRequestError: undefined,
      };
      break;

    case ActionCreators.RequestAvailableServiceTiers.type:
      partialState = {
        isRequestingAvailableServiceTiers: true,
        requestingAvailableServiceTiersError: undefined,
      };
      break;
    case ActionCreators.ReceivedAvailableServiceTiersSuccess.type:
      partialState = {
        availableServiceTiers: action.payload.response,
        requestingAvailableServiceTiersError: undefined,
        isRequestingAvailableServiceTiers: false,
      };
      break;
    case ActionCreators.ReceivedAvailableServiceTiersError.type:
      partialState = {
        requestingAvailableServiceTiersError: action.payload,
        isRequestingAvailableServiceTiers: false,
      };
      break;
    case ActionCreators.ClearReceivedAvailableServiceTiersError.type:
      partialState = {
        requestingAvailableServiceTiersError: undefined,
      };
      break;
      
    case ActionCreators.DeleteServiceTierChangeRequest.type:
      partialState = {
        isDeletingServiceTierChangeRequest: true,
        requestingDeleteServiceTierChangeRequestSuccess: undefined,
        requestingDeleteServiceTierChangeRequestError: undefined,
      };
      break;
    case ActionCreators.ReceivedDeleteServiceTierChangeRequestSuccess.type:
      partialState = {
        requestingDeleteServiceTierChangeRequestSuccess: true,
        requestingDeleteServiceTierChangeRequestError: undefined,
        isDeletingServiceTierChangeRequest: false,
        currentServiceTierChangeRequest: undefined,
      };
      break;
    case ActionCreators.ReceivedDeleteServiceTierChangeRequestError.type:
      partialState = {
        requestingDeleteServiceTierChangeRequestError: action.payload,
        requestingDeleteServiceTierChangeRequestSuccess: false,
        isRequestingAvailableServiceTiers: false,
      };
      break;
    case ActionCreators.ClearReceivedDeleteServiceTierChangeRequestStatus.type:
      partialState = {
        requestingDeleteServiceTierChangeRequestError: undefined,
        requestingDeleteServiceTierChangeRequestSuccess: undefined,
      };
      break;
    default:
      return state;
  }
  return { ...state, ...partialState };
}
